import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, StickyNote, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getNotesByEvent, createNote } from "@/lib/api";
import { toast } from "sonner";

export function ExpandableNotes({ notes: syncNotes, eventId, eventName, canEdit }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [linkedNotes, setLinkedNotes] = useState([]);
  const isLong = syncNotes?.length > 120;

  useEffect(() => {
    if (eventId) {
      getNotesByEvent(eventId)
        .then(res => setLinkedNotes(res.data))
        .catch(() => {});
    }
  }, [eventId]);

  const hasContent = syncNotes || linkedNotes.length > 0;

  const onAddNote = async (e) => {
    e.stopPropagation();
    try {
      const note = await createNote({ title: `${eventName || "Event"} Notes`, linked_event_id: eventId, linked_event_name: eventName });
      navigate(`/notes?select=${note.data.note_id}`);
    } catch (err) {
      toast.error("Failed to create note");
    }
  };

  const onClickNote = (noteId, e) => {
    e.stopPropagation();
    navigate(`/notes?select=${noteId}`);
  };

  if (!hasContent && !canEdit) return null;

  return (
    <div className="mt-2 pt-2 border-t border-border">
      <button
        data-testid={`notes-toggle-${eventId}`}
        className="flex items-center gap-1.5 mb-1 text-left w-full"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notes</span>
        {(syncNotes || linkedNotes.length > 0) && (
          expanded ? <ChevronUp className="w-3 h-3 text-muted-foreground" /> : <ChevronDown className="w-3 h-3 text-muted-foreground" />
        )}
        {linkedNotes.length > 0 && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 ml-auto">
            {linkedNotes.length}
          </Badge>
        )}
      </button>

      {/* Synced notes from GCal/QB */}
      {syncNotes && (
        <p className={`text-xs text-muted-foreground whitespace-pre-wrap ${!expanded && isLong ? 'line-clamp-3' : ''}`}>
          {syncNotes}
        </p>
      )}
      {syncNotes && isLong && !expanded && (
        <button className="text-xs text-primary mt-1 hover:underline" onClick={(e) => { e.stopPropagation(); setExpanded(true); }}>
          Show more
        </button>
      )}

      {/* Linked Production Hub notes */}
      {expanded && linkedNotes.length > 0 && (
        <div className={`space-y-1 ${syncNotes ? 'mt-2 pt-2 border-t border-border/50' : ''}`}>
          {linkedNotes.map(note => (
            <button
              key={note.note_id}
              data-testid={`linked-note-${note.note_id}`}
              className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-secondary/30 transition-colors group"
              onClick={(e) => onClickNote(note.note_id, e)}
            >
              <StickyNote className="w-3 h-3 text-primary shrink-0" />
              <span className="text-xs truncate group-hover:text-primary transition-colors">{note.title || "Untitled"}</span>
            </button>
          ))}
        </div>
      )}

      {/* Quick-add button when expanded or no content */}
      {canEdit && (expanded || !hasContent) && (
        <Button
          data-testid={`add-note-${eventId}`}
          variant="ghost"
          size="sm"
          className="h-6 text-[11px] text-muted-foreground hover:text-primary gap-1 mt-1 px-1"
          onClick={onAddNote}
        >
          <Plus className="w-3 h-3" /> Add note
        </Button>
      )}
    </div>
  );
}
